import React, { ComponentPropsWithRef, CSSProperties } from 'react';
import { Helmet } from 'react-helmet';
import { TypographyVariants } from '@utmost.co/utmost-design-system';
import { tokens, IconButton, Typography, styled, mergeClassNames } from '@beeline.com/bp-design-system';
import { PagePathname } from 'src/utils/hooks/navigation/types';
import { useAbsoluteNavigation } from 'src/utils/hooks/navigation/useNavigation';
export interface PageTitleProps {
  title: React.ReactNode;
  dynamicTitle?: boolean;
  path?: {
    backPath: PagePathname;
    intl: string;
  };
  actions?: React.ReactNode | undefined;
  headerProps?: ComponentPropsWithRef<'header'>;
  subTitle?: React.ReactNode | undefined;
  style?: CSSProperties;
  titleVariant?: TypographyVariants;
  className?: string;
  dataTest?: string;
  sticky?: boolean;
}
const TITLE_TEMPLATE = 'Beeline - %s';
const DEFAULT_TITLE = 'Beeline';
export const PageTitle = ({
  dynamicTitle,
  title: pageTitle,
  titleVariant = 'h2',
  headerProps,
  subTitle,
  path,
  actions,
  style,
  className,
  dataTest,
  sticky
}: PageTitleProps) => {
  const { goToPage } = useAbsoluteNavigation();
  const PageTitleComponent = sticky ? StyledStickyPageTitle : StyledPageTitle;
  return (
    <PageTitleComponent
      data-test={dataTest ?? 'page-header-component'}
      style={style}
      className={mergeClassNames(`page-header-component${className ? ` ${className}` : ''}`, {
        sticky
      })}
    >
      {typeof pageTitle === 'string' && (
        <Helmet titleTemplate={TITLE_TEMPLATE} defaultTitle={DEFAULT_TITLE} defer={!dynamicTitle}>
          {!!pageTitle && <title>{pageTitle}</title>}
        </Helmet>
      )}
      <StyledPageTitleSubTitle>
        <StyledHeader data-test="page-header" {...headerProps}>
          {path?.backPath && (
            <IconButton
              iconName="arrow_back"
              onClick={() => goToPage(path.backPath)}
              dataTest="page-header-back-button"
              tooltipText={path.intl}
            />
          )}
          <div>
            {pageTitle && (
              <Typography dataTest="page-header-title" variant={titleVariant}>
                {pageTitle}
              </Typography>
            )}
            {subTitle && (
              <Typography color={tokens.colorOnSurfaceVariant} dataTest="page-header-sub-title">
                {subTitle}
              </Typography>
            )}
          </div>
        </StyledHeader>
      </StyledPageTitleSubTitle>
      {actions && <StyledActions data-test="page-actions">{actions}</StyledActions>}
    </PageTitleComponent>
  );
};
const StyledPageTitle = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.l};
`;

const StyledStickyPageTitle = styled('div')`
  display: flex;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.l};
  position: sticky;
  top: 0;
  z-index: 999;
`;

const StyledHeader = styled('header')`
  margin-top: ${({ theme }) => theme.spacingSize.m};
  padding: ${({ theme }) => theme.spacingSize.xs};
  background-color: inherit;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacingSize.m};
`;
const StyledPageTitleSubTitle = styled('div')`
  display: flex;
  flex-direction: column;
`;

const StyledActions = styled('div')`
  display: flex;
  flex-direction: row;
  margin-right: ${({ theme }) => theme.spacingSize.xl};
  gap: ${({ theme }) => theme.spacingSize.l};
`;
